import { useQuery } from 'react-query';
import invariant from 'invariant';

import { addQueryToUrl } from 'dpl/util/queryString';

import { PRISMIC_SEARCH_URL } from '../utils/constants';
import usePrismicRefQuery from './usePrismicRefQuery';
import { isPrismicPreviewPage } from '../utils';

export default function usePrismicQuery({
  defaultValue = { data: {} },
  id,
  uid,
  documentType,
  fetchLinks
}) {
  // use base useQuery instead of useResourceQuery
  // so we *don't* use customFetch to make requests
  // or we'll get CORS/header issues from Prismic :/

  const { ref } = usePrismicRefQuery();
  let query;
  let queryKey;

  invariant(
    id || (uid && documentType),
    'usePrismicQuery invoked without required query params'
  );

  if (id) {
    query = `[[at(document.id, "${id}")]]`;
    queryKey = ['document', id];
  } else if (uid && documentType) {
    query = `[[at(my.${documentType}.uid, "${uid}")]]`;
    queryKey = [documentType, uid];
  }

  let queryUrl = addQueryToUrl(
    {
      ref,
      q: query
    },
    PRISMIC_SEARCH_URL
  );

  if (fetchLinks) {
    queryUrl = addQueryToUrl({ fetchLinks }, queryUrl);
  }

  const prismicQuery = useQuery({
    queryKey,
    queryFn: () =>
      window
        .fetch(queryUrl, {
          method: 'GET'
        })
        .then(res => {
          if (!res.ok) {
            return res.json().then(json => {
              throw new Error(json.message);
            });
          }
          return res.json();
        }),
    enabled: Boolean(ref)
  });

  if (prismicQuery.isSuccess && prismicQuery.data.results_size === 0) {
    if (isPrismicPreviewPage()) {
      return {
        value: defaultValue
      };
    }
    throw new Error(`PrismicError: No results found - ${queryKey.join(', ')}`);
  }

  return {
    ...prismicQuery,
    queryKey,
    value: prismicQuery.isSuccess ? prismicQuery.data.results[0] : defaultValue
  };
}
